import React, { useEffect } from 'react';
import SearchFilter from '@alltrails/shared/components/SearchFilter';
import FilterInputFactory from 'components/explore/filters/FilterInputFactory';
import SaveButton from 'components/explore/filters/SaveButton';
import useHypotheticalSearchResultsCount, { SearchResultsCountFunction } from 'hooks/useHypotheticalSearchResultsCount';
import useModalPortalElement from 'hooks/useModalPortalElement';
import useSearchFilters from 'hooks/useSearchFilters';
import { Filter, FilterConfig, FilterInput, FilterRangeUpdates } from 'types/Search';
import { SearchFiltersUtil } from 'utils/search_filters_util';

export const getPillDisplayText = (filter: Filter, activeFilters: string[]): string => {
  if (activeFilters.length === 0) {
    return filter.display;
  }

  switch (filter.type) {
    case FilterInput.RADIO:
      if (activeFilters.length === 1) {
        return activeFilters[0];
      }
      break;
    case FilterInput.CHECKBOX:
      if (activeFilters.length === 1) {
        return `${activeFilters[0]}`
      }
      return `${activeFilters[0]} • ${activeFilters.length - 1}`;
    case FilterInput.SLIDER:
      if (activeFilters.length === 1) {
        const displayFunction = SearchFiltersUtil.getFilterDisplayFunction(filter.config.displayFunction);

        const min = filter.config.formattedRange[0];
        const max = filter.config.formattedRange[1];

        return `${displayFunction(min)} - ${displayFunction(max)}`;
      }
      break;
    default:
      break;
  }

  return filter.display;
};

export type Props = {
  clearRadioFilter?: (filterKey: string, pageName: string) => void;
  filter: Filter;
  handleFilterRadioToggle?: (filterKey: string, filterConfigKey: string) => void;
  handleFilterSet?: (filterKey: string, value: string) => void;
  handleFilterSlider?: (filterKey: string, value: FilterRangeUpdates) => void;
  handleFilterToggle?: (filterKey: string, filterConfigKey: string) => void;
  page?: string;
  popperClassName?: string;
  searchResultsCountFunction: SearchResultsCountFunction;
  resultsCount: number;
};

const FilterPillFactory = ({
  clearRadioFilter,
  filter,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  page,
  popperClassName,
  resultsCount,
  searchResultsCountFunction
}: Props) => {
  const { activeFilters, clearDraftChanges, clearSavedFilters, filtersByKey, saveChanges, updateFilter } = useSearchFilters({
    clearRadioFilter,
    filters: [filter],
    handleFilterRadioToggle,
    handleFilterSet,
    handleFilterSlider,
    handleFilterToggle,
    page
  });

  const isActive = activeFilters.length > 0;
  const displayText = getPillDisplayText(filter, activeFilters);

  const { performHypotheticalSearch, computedResultsCount, resetHypotheticalResultsCount } = useHypotheticalSearchResultsCount({
    resultsCount,
    searchResultsCountFunction
  });

  useEffect(() => {
    performHypotheticalSearch(filtersByKey);
  }, [filtersByKey]);

  const modalElement = useModalPortalElement();

  return (
    <SearchFilter
      clearButtonHandler={() => {
        clearSavedFilters();
        resetHypotheticalResultsCount();
      }}
      hasActiveFilters={isActive}
      modalElement={modalElement}
      modalTitle={filter.display}
      pillButtonText={displayText}
      popperClassName={popperClassName}
      resetCurrentFormStateHandler={() => {
        clearDraftChanges();
        resetHypotheticalResultsCount();
      }}
      saveButtonChildren={<SaveButton resultsCount={computedResultsCount} />}
      saveButtonHandler={saveChanges}
    >
      <FilterInputFactory filter={filtersByKey[filter.key]} onChange={(changes: FilterConfig) => updateFilter(filter.key, changes)} />
    </SearchFilter>
  );
};

export default FilterPillFactory;
